/**
 * Authenticated HTTP client for the Owner management routes (task 18.7).
 *
 * Every management call carries an `Authorization: Bearer <token>` header where
 * the token is a real Cognito ID-token JWT (Req 3.3, 14.3). The admin shell
 * obtains it via a friendly email + password sign-in (Cognito SRP) — see
 * {@link ../admin/useAdminAuth} and {@link ../admin/cognito} — and the wire
 * format here matches what the API Gateway Cognito JWT authorizer expects.
 *
 * The covered routes (from design.md "Owner" surface):
 *   - POST   /admin/gallery          → presigned PUT for an upload (Req 4.1)
 *   - DELETE /admin/gallery/{id}      → delete an image (Req 4.4)
 *   - PUT    /admin/gallery/order     → persist display order (Req 4.5)
 *   - PUT    /admin/pricing           → update pricing (Req 5.4)
 *   - POST/PUT/DELETE /admin/faq      → manage FAQ entries (Req 12.4)
 *   - GET/POST/DELETE /admin/calendars→ manage calendar connections (Req 7.1, 7.9)
 *   - GET    /admin/insights         → engagement metrics for the Insights tab
 *
 * Reads used to populate the editors reuse the public read routes
 * (`GET /gallery`, `/pricing`, `/faq`) but still send the bearer token so the
 * same client can back an owner-only listing later.
 */

import type {
  BookingConflictSummary,
  BookingInquiry,
  CalendarConnection,
  CalendarProvider,
  CollectionHealthProblem,
  DeliveryFailure,
  FaqEntry,
  Image,
  IsoDateTime,
  MetricDay,
  PricingConfig,
  TrafficSource,
} from "@photography-booking/shared";
import { apiUrl, isUnfriendlyMessage } from "./client.js";

/** Discriminated result so callers branch instead of catching. */
export type AdminResult<T> =
  | { ok: true; data: T }
  | { ok: false; code: string; message: string };

/** Fields the owner edits on the pricing form (timestamp is server-owned). */
export type PricingUpdate = Omit<PricingConfig, "updatedAt">;

/** A new or edited FAQ entry submitted by the owner. */
export type FaqInput = Omit<FaqEntry, "faqId"> & { faqId?: string };

/**
 * A booking as returned by `GET /admin/bookings`: the full inquiry minus the
 * server-only single-use action nonce, plus the owner-only conflict summary
 * for its requested day. This mirrors the backend's admin booking view.
 */
export type AdminBooking = Omit<BookingInquiry, "actionNonce"> & {
  conflicts: BookingConflictSummary;
  /**
   * The specific calendar event(s) that overlap this booking's requested slot
   * (owner-only). Present when a connected Google calendar has a conflicting
   * event; empty/absent otherwise. Each entry is a friendly time + title.
   */
  calendarConflicts?: { when: string; title: string }[];
};

/** The presigned upload descriptor returned by `POST /admin/gallery`. */
export interface PresignedUpload {
  /** Pre-authorized URL to PUT the raw bytes to. */
  uploadUrl: string;
  /** Server-assigned id for the image record being created. */
  imageId: string;
}

/** Builds the standard authorized JSON headers for a management call. */
function authHeaders(token: string): Record<string, string> {
  return {
    Authorization: `Bearer ${token}`,
    "Content-Type": "application/json",
    Accept: "application/json",
  };
}

/** Plain-language fallback used whenever nothing friendlier applies. */
const GENERIC_FRIENDLY_MESSAGE =
  "Something went wrong on our end. Please try again in a moment.";

/** A friendly, user-facing error derived from a failed management response. */
interface FriendlyError {
  code: string;
  message: string;
  reference?: string;
}

/**
 * Maps a failed {@link Response} (status + parsed body) to a plain-language
 * error for a non-technical Owner. The backend returns errors as
 * `{ error: { code, message, reference } }`:
 *
 *   - 401/403 → a friendly "session expired" prompt to sign in again.
 *   - A `reference` (server-side 500/INTERNAL_ERROR) → a reassuring message
 *     that surfaces the reference to quote if it keeps happening.
 *   - Any other 5xx → the generic friendly fallback (never a bare status code).
 *   - A backend message that is already friendly → used as-is.
 *   - Anything else (technical or missing message) → the generic fallback.
 *
 * The returned text never contains a raw status number or a route string.
 */
function friendlyErrorFromResponse(
  status: number,
  body: unknown,
): FriendlyError {
  const err = (
    body as {
      error?: { code?: string; message?: string; reference?: string };
    }
  )?.error;
  const reference = err?.reference;

  if (status === 401 || status === 403) {
    return {
      code: err?.code ?? "UNAUTHORIZED",
      message: "Your session has expired. Please sign in again.",
    };
  }

  if (reference) {
    return {
      code: err?.code ?? "INTERNAL_ERROR",
      message: `Something went wrong on our end. Please try again. If it keeps happening, note this reference: ${reference}.`,
      reference,
    };
  }

  if (status >= 500) {
    return { code: err?.code ?? "INTERNAL_ERROR", message: GENERIC_FRIENDLY_MESSAGE };
  }

  if (!isUnfriendlyMessage(err?.message)) {
    return { code: err?.code ?? "REQUEST_FAILED", message: err!.message as string };
  }

  return { code: err?.code ?? "REQUEST_FAILED", message: GENERIC_FRIENDLY_MESSAGE };
}

/**
 * Performs an authorized JSON request and maps the outcome to an
 * {@link AdminResult}. Never throws: transport and non-2xx outcomes become
 * `{ ok: false }`.
 */
async function authedJson<T>(
  token: string,
  path: string,
  init: { method: string; body?: unknown },
): Promise<AdminResult<T>> {
  let response: Response;
  try {
    response = await fetch(apiUrl(path), {
      method: init.method,
      headers: authHeaders(token),
      body: init.body === undefined ? undefined : JSON.stringify(init.body),
    });
  } catch {
    return {
      ok: false,
      code: "NETWORK_ERROR",
      message:
        "We couldn't reach the studio's servers. Check your internet connection and try again.",
    };
  }

  let body: unknown = undefined;
  try {
    body = await response.json();
  } catch {
    // A 204 No Content (or empty body) is fine for mutations.
  }

  if (!response.ok) {
    const friendly = friendlyErrorFromResponse(response.status, body);
    return { ok: false, code: friendly.code, message: friendly.message };
  }

  return { ok: true, data: (body ?? undefined) as T };
}

// ---- Portfolio (gallery) ------------------------------------------------

/** Lists images for the owner editor (reuses the public listing route). */
export function listImages(token: string): Promise<AdminResult<Image[]>> {
  return authedJson<{ images?: Image[] } | Image[]>(token, "/gallery", {
    method: "GET",
  }).then((result) =>
    result.ok
      ? {
          ok: true,
          data: Array.isArray(result.data)
            ? result.data
            : result.data?.images ?? [],
        }
      : result,
  );
}

/**
 * Requests a presigned PUT for a new upload via `POST /admin/gallery`.
 * The owner UI then PUTs the raw file bytes to {@link PresignedUpload.uploadUrl}
 * with {@link uploadToPresignedUrl}.
 */
export function requestImageUpload(
  token: string,
  meta: {
    contentType: string;
    sizeBytes: number;
    fileName?: string;
    /** Optional caption/comment shown under the image in the gallery. */
    caption?: string;
    /** Optional photo credit shown as "Photo by: {value}". */
    photoCredit?: string;
  },
): Promise<AdminResult<PresignedUpload>> {
  return authedJson<PresignedUpload>(token, "/admin/gallery", {
    method: "POST",
    body: meta,
  });
}

/**
 * Uploads raw bytes to a presigned PUT URL. This call is to object storage,
 * not the API, so it carries no bearer token (the URL itself is the grant).
 */
export async function uploadToPresignedUrl(
  uploadUrl: string,
  file: File,
): Promise<AdminResult<null>> {
  try {
    const response = await fetch(uploadUrl, {
      method: "PUT",
      headers: { "Content-Type": file.type },
      body: file,
    });
    if (!response.ok) {
      return {
        ok: false,
        code: "UPLOAD_FAILED",
        message: "We couldn't upload that image. Please try again.",
      };
    }
    return { ok: true, data: null };
  } catch {
    return {
      ok: false,
      code: "NETWORK_ERROR",
      message:
        "We couldn't reach the studio's servers. Check your internet connection and try again.",
    };
  }
}

/**
 * Lists the full library (every published image, included or not) for the
 * admin via `GET /admin/gallery`. Each image carries `includedInGallery` so the
 * editor can toggle gallery membership. Falls back to the public listing shape
 * if the envelope differs.
 */
export function listLibraryImages(token: string): Promise<AdminResult<Image[]>> {
  return authedJson<{ images?: Image[] } | Image[]>(token, "/admin/gallery", {
    method: "GET",
  }).then((result) =>
    result.ok
      ? {
          ok: true,
          data: Array.isArray(result.data)
            ? result.data
            : result.data?.images ?? [],
        }
      : result,
  );
}

/** Includes/excludes an image from the public gallery via `PUT /admin/gallery/{id}`. */
export function setImageInclusion(
  token: string,
  imageId: string,
  includedInGallery: boolean,
): Promise<AdminResult<null>> {
  return authedJson<null>(token, `/admin/gallery/${encodeURIComponent(imageId)}`, {
    method: "PUT",
    body: { includedInGallery },
  });
}

/** Deletes a single image via `DELETE /admin/gallery/{id}` (Req 4.4). */
export function deleteImage(
  token: string,
  imageId: string,
): Promise<AdminResult<null>> {
  return authedJson<null>(token, `/admin/gallery/${encodeURIComponent(imageId)}`, {
    method: "DELETE",
  });
}

/**
 * Selects the homepage hero from an existing library image via
 * `PUT /admin/hero`. The chosen image's rendition becomes the hero backdrop.
 */
export function selectHeroImage(
  token: string,
  imageId: string,
): Promise<AdminResult<{ renditionKey?: string | null }>> {
  return authedJson<{ renditionKey?: string | null }>(token, "/admin/hero", {
    method: "PUT",
    body: { imageId },
  });
}

/** Persists a new display order via `PUT /admin/gallery/order` (Req 4.5). */
export function reorderImages(
  token: string,
  order: string[],
): Promise<AdminResult<null>> {
  return authedJson<null>(token, "/admin/gallery/order", {
    method: "PUT",
    body: { order },
  });
}

// ---- Hero image (site config) ------------------------------------------

/**
 * Reads the current hero image rendition key (reuses the public `GET /hero`
 * route) so the admin view can show the current hero thumbnail. Returns `null`
 * when no hero is configured yet.
 */
export function getHeroRenditionKey(
  token: string,
): Promise<AdminResult<string | null>> {
  return authedJson<{ renditionKey?: string | null }>(token, "/hero", {
    method: "GET",
  }).then((result) =>
    result.ok
      ? { ok: true, data: result.data?.renditionKey ?? null }
      : result,
  );
}

/**
 * Requests a presigned PUT for a new/replacement hero image via
 * `POST /admin/hero`. The admin UI then PUTs the raw bytes to
 * {@link PresignedUpload.uploadUrl} with {@link uploadToPresignedUrl}; the
 * rendition pipeline persists the hero site-config once processing completes.
 */
export function requestHeroUpload(
  token: string,
  meta: { contentType: string; sizeBytes: number; fileName?: string },
): Promise<AdminResult<PresignedUpload>> {
  return authedJson<PresignedUpload>(token, "/admin/hero", {
    method: "POST",
    body: meta,
  });
}

// ---- Pricing ------------------------------------------------------------

/**
 * Reads current pricing to seed the editor. `GET /pricing` wraps the config in
 * a `{ pricing }` envelope (with a default-rates fallback for unset/legacy
 * records), which is unwrapped here so callers get the {@link PricingConfig}.
 */
export function getPricing(token: string): Promise<AdminResult<PricingConfig>> {
  return authedJson<{ pricing?: PricingConfig } | PricingConfig>(token, "/pricing", {
    method: "GET",
  }).then((result) =>
    result.ok
      ? {
          ok: true,
          data:
            result.data && "pricing" in result.data
              ? (result.data.pricing as PricingConfig)
              : (result.data as PricingConfig),
        }
      : result,
  );
}

/** Updates pricing via `PUT /admin/pricing` (Req 5.4). */
export function updatePricing(
  token: string,
  pricing: PricingUpdate,
): Promise<AdminResult<PricingConfig>> {
  return authedJson<PricingConfig>(token, "/admin/pricing", {
    method: "PUT",
    body: pricing,
  });
}

// ---- FAQ ----------------------------------------------------------------

/** Lists FAQ entries to seed the editor (reuses the public route). */
export function listFaqs(token: string): Promise<AdminResult<FaqEntry[]>> {
  return authedJson<{ faqs?: FaqEntry[] } | FaqEntry[]>(token, "/faq", {
    method: "GET",
  }).then((result) =>
    result.ok
      ? {
          ok: true,
          data: Array.isArray(result.data)
            ? result.data
            : result.data?.faqs ?? [],
        }
      : result,
  );
}

/** Adds a FAQ entry via `POST /admin/faq` (Req 12.4). */
export function addFaq(
  token: string,
  entry: FaqInput,
): Promise<AdminResult<FaqEntry>> {
  return authedJson<FaqEntry>(token, "/admin/faq", {
    method: "POST",
    body: entry,
  });
}

/** Edits a FAQ entry via `PUT /admin/faq` (Req 12.4). */
export function updateFaq(
  token: string,
  entry: FaqEntry,
): Promise<AdminResult<FaqEntry>> {
  return authedJson<FaqEntry>(token, "/admin/faq", {
    method: "PUT",
    body: entry,
  });
}

/** Removes a FAQ entry via `DELETE /admin/faq?id=` (Req 12.4). */
export function removeFaq(
  token: string,
  faqId: string,
): Promise<AdminResult<null>> {
  return authedJson<null>(
    token,
    `/admin/faq?id=${encodeURIComponent(faqId)}`,
    { method: "DELETE" },
  );
}

// ---- Bookings (read-only) ----------------------------------------------

/**
 * Lists every booking inquiry (all statuses) for the owner via
 * `GET /admin/bookings`. The backend wraps the list in a `{ bookings }`
 * envelope, which is unwrapped here. Each booking carries an owner-only
 * `conflicts` summary. Accept/deny can be done here via {@link decideAdminBooking}
 * or from the signed links in the owner notification email.
 */
export function listAdminBookings(
  token: string,
): Promise<AdminResult<AdminBooking[]>> {
  return authedJson<{ bookings?: AdminBooking[] } | AdminBooking[]>(
    token,
    "/admin/bookings",
    { method: "GET" },
  ).then((result) =>
    result.ok
      ? {
          ok: true,
          data: Array.isArray(result.data)
            ? result.data
            : result.data?.bookings ?? [],
        }
      : result,
  );
}

// ---- Delivery issues (troubleshooting) ---------------------------------

/**
 * Lists recorded email delivery failures (bounces, spam complaints, and
 * retry-exhausted sends) via `GET /admin/delivery-issues`, newest first. Backs
 * the admin Troubleshooting tab so the Owner can see when a client outcome
 * email failed to reach its recipient (Req 10.4).
 */
export function listDeliveryIssues(
  token: string,
): Promise<AdminResult<DeliveryFailure[]>> {
  return authedJson<{ failures?: DeliveryFailure[] } | DeliveryFailure[]>(
    token,
    "/admin/delivery-issues",
    { method: "GET" },
  ).then((result) =>
    result.ok
      ? {
          ok: true,
          data: Array.isArray(result.data)
            ? result.data
            : result.data?.failures ?? [],
        }
      : result,
  );
}

/**
 * Dismisses a single recorded delivery failure via
 * `DELETE /admin/delivery-issues?id=<failureId>`. Removes only the log entry;
 * it never affects a booking.
 */
export function dismissDeliveryIssue(
  token: string,
  failureId: string,
): Promise<AdminResult<null>> {
  return authedJson<null>(
    token,
    `/admin/delivery-issues?id=${encodeURIComponent(failureId)}`,
    { method: "DELETE" },
  );
}

/**
 * Clears every recorded delivery failure via `DELETE /admin/delivery-issues`
 * (no id). Removes only the log entries; it never affects any booking.
 */
export function clearDeliveryIssues(token: string): Promise<AdminResult<null>> {
  return authedJson<null>(token, "/admin/delivery-issues", {
    method: "DELETE",
  });
}

/**
 * Sends a free-text message to a booking's client via
 * `POST /admin/bookings/{id}/message`.
 *
 * The message is delivered from (and replies routed back to) the shared studio
 * address, so the Owner can converse with a booker without disclosing her
 * personal email address.
 */
export function messageBookingClient(
  token: string,
  bookingId: string,
  message: string,
): Promise<AdminResult<{ sent: boolean }>> {
  return authedJson<{ sent: boolean }>(
    token,
    `/admin/bookings/${encodeURIComponent(bookingId)}/message`,
    { method: "POST", body: { message } },
  );
}

// ---- Blocked dates (owner-closed days) ---------------------------------

/** Lists the dates the Owner has closed for booking. */
export function listBlockedDates(
  token: string,
): Promise<AdminResult<string[]>> {
  return authedJson<{ dates?: string[] }>(token, "/admin/blocked-dates", {
    method: "GET",
  }).then((result) =>
    result.ok ? { ok: true, data: result.data?.dates ?? [] } : result,
  );
}

/**
 * Closes an inclusive date range for booking so the public calendar shows those
 * days unavailable. Pass the same value for `from` and `to` to close one day.
 */
export function blockDates(
  token: string,
  from: string,
  to: string,
): Promise<AdminResult<string[]>> {
  return authedJson<{ dates?: string[] }>(token, "/admin/blocked-dates", {
    method: "POST",
    body: { from, to },
  }).then((result) =>
    result.ok ? { ok: true, data: result.data?.dates ?? [] } : result,
  );
}

/** Re-opens an inclusive range of previously blocked dates. */
export function unblockDates(
  token: string,
  from: string,
  to: string,
): Promise<AdminResult<string[]>> {
  const query = `from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`;
  return authedJson<{ dates?: string[] }>(
    token,
    `/admin/blocked-dates?${query}`,
    { method: "DELETE" },
  ).then((result) =>
    result.ok ? { ok: true, data: result.data?.dates ?? [] } : result,
  );
}

/** The accept/deny action the owner applies to a PENDING inquiry. */
export type BookingDecisionAction = "ACCEPT" | "DENY";

/** Editable fields for an admin booking override (all optional). */
export interface AdminBookingUpdate {
  clientName?: string;
  clientEmail?: string;
  requestedDate?: string;
  startTime?: string;
  durationMinutes?: number;
  message?: string;
  status?: BookingInquiry["status"];
}

/**
 * Edits an existing inquiry via `PUT /admin/bookings/{id}` (owner override).
 * Can change the schedule, contact details, message, and/or status (including
 * reversing a terminal decision). On a status change into APPROVED/DENIED the
 * backend also emails the client. Returns the updated booking (minus the
 * server-only nonce and conflicts) from the `{ booking }` envelope.
 */
export function updateAdminBooking(
  token: string,
  bookingId: string,
  updates: AdminBookingUpdate,
  notify: boolean,
): Promise<AdminResult<Omit<AdminBooking, "conflicts">>> {
  return authedJson<
    { booking?: Omit<AdminBooking, "conflicts"> } | Omit<AdminBooking, "conflicts">
  >(token, `/admin/bookings/${encodeURIComponent(bookingId)}`, {
    method: "PUT",
    body: { ...updates, notify },
  }).then((result) =>
    result.ok
      ? {
          ok: true,
          data:
            result.data && "booking" in result.data
              ? (result.data.booking as Omit<AdminBooking, "conflicts">)
              : (result.data as Omit<AdminBooking, "conflicts">),
        }
      : result,
  );
}

/**
 * Accepts or denies a PENDING inquiry via
 * `POST /admin/bookings/{id}/decision` (the session-authenticated counterpart
 * to the signed email links). On success the backend applies the transition
 * and sends the client outcome email; the updated booking (minus conflicts) is
 * returned in a `{ booking }` envelope, unwrapped here.
 */
export function decideAdminBooking(
  token: string,
  bookingId: string,
  action: BookingDecisionAction,
  notify: boolean,
  calendarEventTitle?: string,
): Promise<AdminResult<Omit<AdminBooking, "conflicts">>> {
  return authedJson<
    { booking?: Omit<AdminBooking, "conflicts"> } | Omit<AdminBooking, "conflicts">
  >(token, `/admin/bookings/${encodeURIComponent(bookingId)}/decision`, {
    method: "POST",
    body: {
      action,
      notify,
      ...(calendarEventTitle ? { calendarEventTitle } : {}),
    },
  }).then((result) =>
    result.ok
      ? {
          ok: true,
          data:
            result.data && "booking" in result.data
              ? (result.data.booking as Omit<AdminBooking, "conflicts">)
              : (result.data as Omit<AdminBooking, "conflicts">),
        }
      : result,
  );
}

/**
 * Permanently deletes an inquiry via `DELETE /admin/bookings/{id}`. Idempotent
 * on the backend; no client email is sent.
 */
export function deleteAdminBooking(
  token: string,
  bookingId: string,
  notify: boolean,
): Promise<AdminResult<null>> {
  return authedJson<null>(
    token,
    `/admin/bookings/${encodeURIComponent(bookingId)}?notify=${notify ? "true" : "false"}`,
    { method: "DELETE" },
  );
}

// ---- Calendar connections ----------------------------------------------

/**
 * Begins the one-click Google Calendar connect: asks the backend for the Google
 * consent URL (with a signed CSRF state) via
 * `GET /admin/calendars/google/authorize`. The caller then redirects the
 * browser to that URL.
 */
export function getGoogleAuthUrl(
  token: string,
): Promise<AdminResult<{ url: string }>> {
  return authedJson<{ url: string }>(
    token,
    "/admin/calendars/google/authorize",
    { method: "GET" },
  );
}

/**
 * A connection as returned by `GET /admin/calendars`. Google connections carry
 * the linked account's email (the primary calendar's id) so the Owner can
 * verify *which* account is connected.
 */
export type AdminCalendarConnection = CalendarConnection & {
  accountEmail?: string;
  accountTimeZone?: string;
};

/** Lists calendar connections via `GET /admin/calendars` (Req 7.1). */
export function listCalendars(
  token: string,
): Promise<AdminResult<AdminCalendarConnection[]>> {
  return authedJson<
    { connections?: AdminCalendarConnection[] } | AdminCalendarConnection[]
  >(
    token,
    "/admin/calendars",
    { method: "GET" },
  ).then((result) =>
    result.ok
      ? {
          ok: true,
          data: Array.isArray(result.data)
            ? result.data
            : result.data?.connections ?? [],
        }
      : result,
  );
}

/**
 * Connects a calendar via `POST /admin/calendars` (Req 7.1).
 *
 * `credentials` are the provider-specific fields the backend stores in Secrets
 * Manager (never persisted in the browser): Google `{ clientId, clientSecret,
 * refreshToken, calendarId? }`, iCloud `{ username, appSpecificPassword,
 * calendarUrl }`, Outlook `{ tenantId, clientId, clientSecret, refreshToken,
 * scheduleAddress }`. The server responds with the created connection only —
 * never the credentials. The backend wraps the created connection in a
 * `{ connection }` envelope, which is unwrapped here.
 */
export function connectCalendar(
  token: string,
  provider: CalendarProvider,
  credentials: Record<string, string>,
): Promise<AdminResult<CalendarConnection>> {
  return authedJson<{ connection?: CalendarConnection } | CalendarConnection>(
    token,
    "/admin/calendars",
    { method: "POST", body: { provider, credentials } },
  ).then((result) =>
    result.ok
      ? {
          ok: true,
          data:
            "connection" in result.data
              ? (result.data.connection as CalendarConnection)
              : (result.data as CalendarConnection),
        }
      : result,
  );
}

/** A single owner calendar event for the read-only admin calendar view. */
export interface CalendarEventView {
  id: string;
  title: string;
  start: string;
  end: string;
  allDay: boolean;
  /**
   * The event's own IANA timezone (e.g. "America/New_York"), when the provider
   * reports one. Used to render the event's wall-clock time in its own zone
   * rather than the viewer's browser zone.
   */
  timeZone?: string;
}

/**
 * Lists the owner's own events from a connected calendar via
 * `GET /admin/calendars/{id}/events` for the read-only admin view. Owner-only;
 * never public. The backend returns a `{ provider, events }` envelope.
 */
export function listCalendarEvents(
  token: string,
  connectionId: string,
): Promise<AdminResult<CalendarEventView[]>> {
  return authedJson<{ events?: CalendarEventView[] }>(
    token,
    `/admin/calendars/${encodeURIComponent(connectionId)}/events`,
    { method: "GET" },
  ).then((result) =>
    result.ok ? { ok: true, data: result.data?.events ?? [] } : result,
  );
}

/** Disconnects a calendar via `DELETE /admin/calendars/{id}` (Req 7.9). */
export function disconnectCalendar(
  token: string,
  connectionId: string,
): Promise<AdminResult<null>> {
  return authedJson<null>(
    token,
    `/admin/calendars/${encodeURIComponent(connectionId)}`,
    { method: "DELETE" },
  );
}

// ---- Insights (engagement metrics) -------------------------------------

/** The preset day counts the Insights tab offers as single-click choices. */
export type InsightsRangeDays = 7 | 30 | 90;

/**
 * What the tab's range buttons set: a preset day count ending today, or year to
 * date. The value is passed straight through as the `days` query parameter so
 * the year boundary is resolved server-side in `America/New_York` and the
 * browser's own timezone never enters into it.
 */
export type InsightsRangeSelector = InsightsRangeDays | "ytd";

/**
 * How a caller selects the reported window. Either one of the preset
 * selectors ending today (bare, or wrapped as `{ days }`), or an explicit
 * inclusive `Metric_Day` range. Omitting the range entirely lets the backend
 * apply its default of the last 30 days.
 */
export type InsightsRange =
  | InsightsRangeSelector
  | { days: InsightsRangeSelector }
  | { start: MetricDay; end: MetricDay };

/**
 * One Metric_Day's counts, as plotted on the per-day trend charts. Every
 * plotted series reads off this entry, so a chart and its headline total are
 * the same numbers summed one way or the other.
 */
export interface InsightsDay {
  /** `YYYY-MM-DD` in `America/New_York`. */
  date: MetricDay;
  siteViews: number;
  /** Approximate: a visitor returning on a later day counts once per day. */
  uniqueVisitors: number;
  /**
   * Site views on this Metric_Day per Traffic_Source. Every source is present;
   * the values sum to this day's `siteViews` (Req 12.8).
   */
  trafficSources: Record<TrafficSource, number>;
  /**
   * Each Booking_Funnel stage count on this Metric_Day, keyed by stage key.
   * Site views and booking forms started come from the day's summary; the two
   * inquiry stages come from the live per-day booking buckets (Req 12.9).
   */
  funnel: Record<string, number>;
}

/** Range-wide totals across every Metric_Day in the reported window. */
export interface InsightsTotals {
  siteViews: number;
  uniqueVisitors: number;
  bookingFormsStarted: number;
  inquiriesSubmitted: number;
  inquiriesApproved: number;
}

/** One row of "Photos people looked at most". */
export interface InsightsTopImage {
  imageId: string;
  opens: number;
  caption?: string;
  /** True when the image is no longer in the portfolio. */
  removed: boolean;
}

/** "From visit to booking": stage counts plus the drop-off between them. */
export interface InsightsFunnel {
  stages: { key: string; label: string; count: number }[];
  /** Omitted for a pair whose earlier stage is 0, so no divide-by-zero rate. */
  conversions: { from: string; to: string; percent: number }[];
  /** True when a later stage exceeds an earlier one (uncounted visits). */
  hasUnmeasuredVisits: boolean;
}

/** Whether measurement itself looks healthy, for the warning banners. */
export interface InsightsHealth {
  /** Instant of the most recent recorded event, or null if none ever. */
  lastEventAt: IsoDateTime | null;
  /**
   * True when the two most recent complete Metric_Days are both empty and
   * nothing has been recorded since them.
   */
  measurementMayHaveStopped: boolean;
  /** Problems recorded in the last 7 Metric_Days, most recent first. */
  problems: CollectionHealthProblem[];
}

/**
 * Everything the Insights tab renders, in one response (Req 12.5): per-day
 * site views and approximate unique visitors, Traffic_Source counts, the image
 * popularity list, the Booking_Funnel stages and the collection health status.
 */
export interface InsightsPayload {
  range: { start: MetricDay; end: MetricDay };
  /**
   * One entry per Metric_Day in the range, ascending, gaps filled with 0. Each
   * entry also carries that day's own `trafficSources` and `funnel` counts, so
   * this array is the single source for every plotted series (Req 12.8, 12.9).
   */
  days: InsightsDay[];
  totals: InsightsTotals;
  trafficSources: { source: TrafficSource; siteViews: number }[];
  topImages: InsightsTopImage[];
  funnel: InsightsFunnel;
  health: InsightsHealth;
}

/** Serializes a range selection into the `GET /admin/insights` query string. */
function insightsQuery(range?: InsightsRange): string {
  if (!range) return "";
  if (typeof range === "number" || typeof range === "string") {
    return `?days=${encodeURIComponent(String(range))}`;
  }
  if ("days" in range) return `?days=${encodeURIComponent(String(range.days))}`;
  const start = encodeURIComponent(range.start);
  const end = encodeURIComponent(range.end);
  return `?start=${start}&end=${end}`;
}

/**
 * Reads engagement metrics for a range via `GET /admin/insights` (Req 12.5).
 *
 * `range` is a selector — `7 | 30 | 90 | "ytd"`, bare or wrapped as
 * `{ days }`, passed straight through as the `days` query parameter so the
 * year boundary resolves server-side in `America/New_York` — or an explicit
 * `{ start, end }` pair of `YYYY-MM-DD` Metric_Days; omit it to get the
 * backend default of the last 30 Metric_Days. Like every other call here it
 * never throws — transport and non-2xx outcomes come back as `{ ok: false }`
 * with a plain-language message the tab can show as-is.
 */
export function fetchInsights(
  token: string,
  range?: InsightsRange,
): Promise<AdminResult<InsightsPayload>> {
  return authedJson<InsightsPayload>(
    token,
    `/admin/insights${insightsQuery(range)}`,
    { method: "GET" },
  );
}
